import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  styled,
  alpha,
  useTheme,
  TextField,
  Button,
  Chip,
  InputAdornment,
  Collapse,
  Menu,
  ListItemIcon,
  ListItemText,
  MenuItem,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import NotificationSnackbar from "./NotificationBar";
import ConfirmationDialog from "./ConfirmationDialog";

const ResponsiveTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: "600px",
  overflowX: "auto",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "12px",
  "&::-webkit-scrollbar": { height: "8px", width: "8px" },
  "&::-webkit-scrollbar-track": {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: alpha(theme.palette.primary.main, 0.25),
    borderRadius: "4px",
    "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.4) },
  },
}));

function UserRoles() {
  const theme = useTheme();
  const PRIMARY_COLOR = "#0a2e52";
  const SECONDARY_COLOR = "#1565C0";
  const HEADER_BG_COLOR = "#ebeef1";
  const [searchTerm, setSearchTerm] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showData, setShowData] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const actionMenuOpen = Boolean(anchorEl);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleSearch = async () => {
    const term = searchTerm.trim();
    if (!term) {
      setShowData(false);
      setUsers([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const res = await fetch(`/api/support/user-by-search-terms`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ searchKeyword: term }),
      });

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        const msg = (data && data.message) || `HTTP ${res.status}`;
        setError(msg);
        setShowData(true);
        setUsers([]);
        return;
      }

      const list = Array.isArray(data) ? data : [];
      if (list.length > 0) {
        setUsers(list);
        setShowData(true);
        setError(null);
      } else {
        setUsers([]);
        setShowData(true);
        setError("No users found matching your search term.");
      }
    } catch (err) {
      console.error("Search failed:", err);
      setError("Search failed. Please try again.");
      setShowData(true);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setError(null);
    setShowData(false);
    setUsers([]);
  };

  const handleMenuOpen = (event, user) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleConfirmDelete = async () => {
    if (!selectedUser) return;

    try {
      setIsDeleting(true);
      const response = await fetch(`/api/support/remove-user-role`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          entitySectorId: selectedUser.entitySectorId,
          userId: selectedUser.userId || selectedUser.id,
          roleId: selectedUser.roleId,
        }),
      });

      if (response.ok) {
        setSnackbarMessage(
          `User ${selectedUser.Name} role removed successfully!`,
        );
        setSnackbarSeverity("success");
        setSnackbarOpen(true);

        setUsers((prevUsers) =>
          prevUsers.filter((u) => u.id !== selectedUser.id),
        );
        setDeleteDialogOpen(false);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setSnackbarMessage(errorData.message || "Failed to remove user role.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("Error deleting user role:", error);
      setSnackbarMessage("An error occurred. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: "16px",
        p: { xs: 2, sm: 3 },
        mb: 3,
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
        minHeight: "75vh",
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "stretch", md: "center" },
          mb: 4,
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, color: PRIMARY_COLOR }}>
          User Roles Management
        </Typography>
      </Box>

      {/* SEARCH CARD */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 3,
          border: `1px solid ${alpha(PRIMARY_COLOR, 0.1)}`,
          bgcolor: alpha(PRIMARY_COLOR, 0.015),
        }}
      >
        <Typography
          variant="h6"
          sx={{ mb: 1, color: PRIMARY_COLOR, fontWeight: 600 }}
        >
          Search Users
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
          Enter a search term (e.g. name, email, or UserId) to find users and
          manage their roles.
        </Typography>

        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "center",
            mb: 3,
          }}
        >
          <TextField
            placeholder="Search users..."
            size="small"
            value={searchTerm}
            onChange={(e) => {
              const v = e.target.value;
              setSearchTerm(v);
              if (!v.trim()) setShowData(false);
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {searchTerm && (
                    <IconButton
                      aria-label="clear"
                      size="small"
                      onClick={handleClearSearch}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
            sx={{
              flexGrow: 1,
              maxWidth: 500,
              bgcolor: "white",
              borderRadius: 1,
            }}
          />
          <Button
            variant="contained"
            disableElevation
            startIcon={
              loading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <SearchIcon />
              )
            }
            onClick={handleSearch}
            disabled={loading}
            sx={{
              textTransform: "none",
              px: 4,
              py: 1,
              bgcolor: PRIMARY_COLOR,
              "&:hover": { bgcolor: SECONDARY_COLOR },
            }}
          >
            {loading ? "Searching..." : "Search"}
          </Button>
        </Box>

        <Collapse in={showData} timeout="auto">
          {error ? (
            <Typography
              color="error"
              sx={{ fontSize: "0.95rem", fontWeight: 500 }}
            >
              {error}
            </Typography>
          ) : users && users.length > 0 ? (
            <Box>
              <Typography
                sx={{ fontWeight: 600, color: PRIMARY_COLOR, mb: 1.5 }}
              >
                Found {users.length} User{users.length > 1 ? "s" : ""}
              </Typography>
              <ResponsiveTableContainer sx={{ maxHeight: 500 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, bgcolor: HEADER_BG_COLOR }}>
                        ID
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: HEADER_BG_COLOR }}>
                        Name
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: HEADER_BG_COLOR }}>
                        Phone Number
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: HEADER_BG_COLOR }}>
                        Email
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: HEADER_BG_COLOR }}>
                        Institution
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: HEADER_BG_COLOR }}>
                        Role
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, bgcolor: HEADER_BG_COLOR }}>
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((u) => (
                      <TableRow key={u.id} hover>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {u.id ?? "-"}
                        </TableCell>
                        <TableCell>{u.Name || "-"}</TableCell>
                        <TableCell>{u.phoneNumber || "-"}</TableCell>
                        <TableCell>{u.email || "-"}</TableCell>
                        
                        {/* COMBINED INSTITUTION AND SECTOR CELL */}
                        <TableCell>
                          {u.institution || u.sector ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                              {u.institution && (
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                  {u.institution}
                                </Typography>
                              )}
                              {u.sector && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: SECONDARY_COLOR,
                                    bgcolor: alpha(SECONDARY_COLOR, 0.08),
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: '6px',
                                    fontWeight: 500,
                                    border: `1px solid ${alpha(SECONDARY_COLOR, 0.2)}`,
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {u.sector}
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            "-"
                          )}
                        </TableCell>

                        <TableCell>
                          <Chip
                            label={u.Role || "None"}
                            size="small"
                            sx={{
                              bgcolor: alpha(SECONDARY_COLOR, 0.1),
                              color: SECONDARY_COLOR,
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            onClick={(e) => handleMenuOpen(e, u)}
                            size="small"
                            sx={{
                              color: PRIMARY_COLOR,
                              backgroundColor: alpha(PRIMARY_COLOR, 0.05),
                              "&:hover": {
                                backgroundColor: alpha(PRIMARY_COLOR, 0.15),
                              },
                            }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ResponsiveTableContainer>

              <Menu
                anchorEl={anchorEl}
                open={actionMenuOpen}
                onClose={handleMenuClose}
                PaperProps={{
                  elevation: 0,
                  sx: {
                    overflow: "visible",
                    filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.15))",
                    mt: 1,
                    minWidth: 160,
                    borderRadius: 2,
                    "& .MuiMenuItem-root": {
                      px: 2,
                      py: 1.2,
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.error.main, 0.04),
                      },
                    },
                  },
                }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              >
                <MenuItem onClick={handleDeleteClick}>
                  <ListItemIcon>
                    <DeleteIcon
                      fontSize="small"
                      sx={{ color: theme.palette.error.main }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary="Remove Role"
                    primaryTypographyProps={{
                      fontWeight: 500,
                      fontSize: "0.9rem",
                      color: theme.palette.error.main,
                    }}
                  />
                </MenuItem>
              </Menu>
            </Box>
          ) : (
            <Typography sx={{ color: "text.secondary" }}>
              No results found.
            </Typography>
          )}
        </Collapse>
      </Paper>

      {/* REUSABLE DELETE CONFIRMATION DIALOG */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        handleClose={() => !isDeleting && setDeleteDialogOpen(false)}
        handleConfirm={handleConfirmDelete}
        title="Confirm Removal"
        content={
          <>
            Are you sure you want to remove the role from user{" "}
            <strong>{selectedUser?.Name}</strong>? This action cannot be
            reverted.
          </>
        }
        confirmText="Remove Role"
        cancelText="Cancel"
        loading={isDeleting}
        severity="error"
      />

      {/* SNACKBAR NOTIFICATIONS */}
      <NotificationSnackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Box>
  );
}

export default UserRoles;